import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Instagram, Youtube, ExternalLink, Trophy, GraduationCap, Ruler, MapPin, Users, Play, Mail } from "lucide-react";
import PageLayout from "../components/layout/PageLayout";
import api from "../lib/api";

export default function AthleteProfile() {
    const { id } = useParams();
    const [a, setA] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get(`/athletes/${id}`).then(r => setA(r.data)).catch(e => setError(e?.response?.data?.detail || "Not found"));
    }, [id]);

    if (error) return <PageLayout><div className="pt-40 pb-20 text-center text-white/50" data-testid="profile-error">{String(error)}</div></PageLayout>;
    if (!a) return <PageLayout><div className="pt-40 pb-20 text-center text-white/50">Loading…</div></PageLayout>;

    return (
        <PageLayout>
            {/* HERO */}
            <section className="relative min-h-[80vh] flex items-end pb-14 pt-24 overflow-hidden" data-testid="athlete-profile-hero">
                <img src={a.hero_image} alt={a.name} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/40" />
                <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 w-full">
                    <div className="section-label mb-4">Digital Beast Athlete</div>
                    <div className="flex flex-wrap gap-6 items-end justify-between">
                        <div>
                            <h1 className="font-display text-6xl md:text-8xl text-white leading-[0.85] tracking-tight" data-testid="athlete-name">
                                {a.name.toUpperCase()}
                            </h1>
                            <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-white/80 text-sm uppercase tracking-widest">
                                <span>{a.sport}</span>
                                <span>·</span>
                                <span>{a.position}</span>
                                <span>·</span>
                                <span className="text-[#d4af37]">#{a.jersey}</span>
                                <span>·</span>
                                <span>{a.school}</span>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <Link to="/contact" className="btn-gold" data-testid="request-sponsor-btn">Request Sponsorship</Link>
                            <Link to="/contact" className="btn-outline" data-testid="book-appearance-btn">Book Appearance</Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* QUICK STATS */}
            <section className="border-b border-white/10 bg-[#0a0a0a]">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 grid grid-cols-2 md:grid-cols-5 gap-6">
                    {[
                        { icon: MapPin, l: "Hometown", v: a.hometown || "—" },
                        { icon: Ruler, l: "Height / Weight", v: `${a.height} · ${a.weight}` },
                        { icon: GraduationCap, l: "Class of", v: a.grad_year || "—" },
                        { icon: Trophy, l: "Conference", v: a.conference || "—" },
                        { icon: Users, l: "Followers", v: (a.social?.followers || 0).toLocaleString() },
                    ].map(({icon: I, l, v}) => (
                        <div key={l} className="flex items-start gap-3" data-testid={`quick-stat-${l.toLowerCase().replace(/[\s/]+/g,'-')}`}>
                            <I size={18} className="text-[#d4af37] mt-1" strokeWidth={1.5} />
                            <div>
                                <div className="text-white/40 text-[10px] uppercase tracking-[0.24em]">{l}</div>
                                <div className="text-white text-base mt-1 font-display tracking-wide">{v}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="py-16">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-8 space-y-14">
                        {/* BIO */}
                        <div>
                            <div className="section-label mb-5">The Athlete</div>
                            <p className="text-white/80 text-lg leading-relaxed">{a.bio}</p>
                        </div>

                        {/* Stats */}
                        {a.stats && Object.keys(a.stats).length > 0 && (
                            <div>
                                <div className="section-label mb-5">Performance</div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {Object.entries(a.stats).map(([k, v]) => (
                                        <div key={k} className="surface p-5">
                                            <div className="text-[#d4af37] font-display text-3xl md:text-4xl">{v}</div>
                                            <div className="text-white/50 text-xs uppercase tracking-widest mt-2">{k.replaceAll("_"," ")}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Achievements */}
                        {a.achievements?.length > 0 && (
                            <div>
                                <div className="section-label mb-5">Achievements</div>
                                <ul className="space-y-2">
                                    {a.achievements.map((x) => (
                                        <li key={x} className="flex items-start gap-3 text-white/80 leading-relaxed" data-testid="achievement-item">
                                            <Trophy size={14} className="text-[#d4af37] mt-1 flex-shrink-0" /> {x}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Highlight */}
                        {a.highlight_url && (
                            <div>
                                <div className="section-label mb-5">Highlight Reel</div>
                                <a href={a.highlight_url} target="_blank" rel="noreferrer" className="surface surface-hover flex items-center gap-4 p-6 group" data-testid="highlight-link">
                                    <div className="w-14 h-14 border border-[#d4af37] flex items-center justify-center">
                                        <Play size={20} className="text-[#d4af37]" />
                                    </div>
                                    <div>
                                        <div className="font-display text-xl text-white">Watch Full Highlight</div>
                                        <div className="text-white/50 text-xs mt-1">Opens external</div>
                                    </div>
                                    <ExternalLink size={16} className="ml-auto text-white/40 group-hover:text-[#d4af37]" />
                                </a>
                            </div>
                        )}

                        {/* Interests & Causes */}
                        {(a.interests?.length > 0 || a.causes?.length > 0) && (
                            <div className="grid md:grid-cols-2 gap-8">
                                {a.interests?.length > 0 && (
                                    <div>
                                        <div className="section-label mb-4">Interests</div>
                                        <div className="flex flex-wrap gap-2">
                                            {a.interests.map(x => <span key={x} className="chip">{x}</span>)}
                                        </div>
                                    </div>
                                )}
                                {a.causes?.length > 0 && (
                                    <div>
                                        <div className="section-label mb-4">Community Causes</div>
                                        <div className="flex flex-wrap gap-2">
                                            {a.causes.map(x => <span key={x} className="chip">{x}</span>)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* SIDEBAR */}
                    <aside className="lg:col-span-4 space-y-4">
                        <div className="surface p-6 beam-card" data-testid="partnership-card">
                            <div className="section-label mb-3">Partnership Status</div>
                            <div className="font-display text-2xl text-white capitalize">{a.partnership_status}</div>
                            {a.partnership_categories?.length > 0 && (
                                <div className="mt-5">
                                    <div className="text-white/40 text-[10px] uppercase tracking-[0.24em] mb-3">Preferred Categories</div>
                                    <div className="flex flex-wrap gap-2">
                                        {a.partnership_categories.map(c => <span key={c} className="chip">{c}</span>)}
                                    </div>
                                </div>
                            )}
                            <Link to="/contact" className="btn-gold w-full justify-center mt-6" data-testid="sidebar-request-btn">
                                <Mail size={14} /> Request Partnership
                            </Link>
                        </div>

                        {a.social && (
                            <div className="surface p-6">
                                <div className="section-label mb-4">Social</div>
                                <div className="space-y-3">
                                    {a.social.instagram && (
                                        <a href={`https://instagram.com/${a.social.instagram.replace('@','')}`} target="_blank" rel="noreferrer"
                                            className="flex items-center gap-3 text-white/80 hover:text-[#d4af37] transition-colors" data-testid="social-instagram">
                                            <Instagram size={18} /> {a.social.instagram}
                                        </a>
                                    )}
                                    {a.social.tiktok && (
                                        <div className="flex items-center gap-3 text-white/80" data-testid="social-tiktok">
                                            <div className="w-[18px] h-[18px] flex items-center justify-center font-mono text-sm">◈</div> {a.social.tiktok}
                                        </div>
                                    )}
                                    {a.social.youtube && (
                                        <a href={`https://youtube.com/${a.social.youtube}`} target="_blank" rel="noreferrer"
                                            className="flex items-center gap-3 text-white/80 hover:text-[#d4af37] transition-colors" data-testid="social-youtube">
                                            <Youtube size={18} /> {a.social.youtube}
                                        </a>
                                    )}
                                    <div className="border-t border-white/10 pt-3">
                                        <div className="text-[#d4af37] font-display text-2xl">{(a.social.followers || 0).toLocaleString()}</div>
                                        <div className="text-white/40 text-xs uppercase tracking-widest">Combined followers</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {a.academics?.length > 0 && (
                            <div className="surface p-6">
                                <div className="section-label mb-4">Academics</div>
                                <ul className="space-y-2 text-white/80 text-sm">
                                    {a.academics.map((x) => <li key={x} className="flex items-start gap-2"><GraduationCap size={14} className="text-[#d4af37] mt-0.5 flex-shrink-0" /> {x}</li>)}
                                </ul>
                            </div>
                        )}
                    </aside>
                </div>
            </section>
        </PageLayout>
    );
}
